import { confirmInGrain } from "../utils/grainDialogs.js";
import React from "react";
import {
  ArrowDown,
  ArrowUp,
  Bell,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  History,
  List,
  Mail,
  LoaderCircle,
  Pause,
  Pencil,
  Play,
  Plus,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router";
import useAccess from "../hooks/useAccess.jsx";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import { useToast } from "./ResultsPrimitives.jsx";
import {
  applyAutomationOperation,
  createAutomation,
  deleteAutomation,
  fetchAutomationCalendar,
  fetchAutomationCatalog,
  fetchAutomationHealth,
  fetchAutomationRuns,
  fetchAutomations,
  fetchAutomationTargets,
  previewAutomation,
  runAutomationNow,
  updateAutomation,
} from "../lib/automationsApi.js";
import { canManageAutomations, canSendReportsEmail } from "../lib/accessControl.js";
import ReportEmailComposer from "./ReportEmailComposer.jsx";
import { ReportEmailRunDetails } from "./ReportEmailActions.jsx";
import { REPORT_EMAIL_LABELS, REPORT_EMAIL_TYPE, applyReportEmailCompartmentAccess, defaultReportEmailAction, reportEmailValidation, serializeReportEmailAction } from "../utils/reportEmailForm.js";
import { resolvePageSurfaceWidthClassName } from "../utils/pageSurfaceLayout.js";
import {
  BILLING_PENDING_CONDITION_TYPE,
  acceptGeneratedNotificationBodyPatch,
  applyBillingMessageCriteria,
  buildBillingNotificationMessage,
  buildBillingPendingCondition,
  initializeNotificationMessageState,
  keepCustomNotificationBodyPatch,
  MISSING_ACTUAL_TONNAGE_CATEGORIES,
  normalizeBillingCategories,
  notificationActionsHaveEligibleRecipients,
  notificationBodyEditPatch,
  pruneIneligibleNotificationRecipients,
  splitAutomationTargets,
  stripAutomationActionClientState,
  toggleBillingCategoryGroup,
} from "../utils/automationNotificationForm.js";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import AutomationDateCreationMenu from "./AutomationDateCreationMenu.jsx";
import AutomationTaskContextMenu from "./AutomationTaskContextMenu.jsx";
import { joinClasses } from "./resultsPrimitives/shared.js";

const COMPANY_TIMEZONE = "America/Toronto";
const PAGE_WIDTH_CLASS_NAME = resolvePageSurfaceWidthClassName("automations");
const FIELD_CLASS_NAME = "h-9 w-full rounded-[4px] border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 outline-hidden transition focus:border-accent focus:ring-2 focus:ring-accent/15";
const LABEL_CLASS_NAME = "grid content-start gap-1 text-[11px] font-bold text-slate-700";
const SECONDARY_BUTTON_CLASS_NAME = "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[4px] border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-100";
const DANGER_BUTTON_CLASS_NAME = "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[4px] border border-red-300 bg-white px-2.5 py-1.5 text-xs font-bold text-red-700 transition hover:border-red-400 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300/50 disabled:cursor-not-allowed disabled:opacity-50";
const PRIMARY_BUTTON_CLASS_NAME = "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[4px] border border-accent bg-accent px-3 py-1.5 text-xs font-black text-white shadow-sm transition hover:border-accent-strong hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-50";
const NOTIFICATION_BUTTON_CLASS_NAME = "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[4px] border border-blue-200 bg-white px-3 py-1.5 text-xs font-black text-blue-800 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50";
const NEW_AUTOMATION_BUTTON_CLASS_NAME = "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[4px] border px-3 py-1.5 text-xs font-black transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:ring-offset-2";
const NEW_AUTOMATION_BUTTON_IDLE_CLASS_NAME = "border-slate-300 bg-white text-slate-700 shadow-none hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900";
const NEW_AUTOMATION_BUTTON_SELECTED_CLASS_NAME = "border-accent-strong bg-accent-strong text-white shadow-[0_4px_12px_rgba(15,92,87,0.24)] ring-2 ring-accent/25 ring-offset-2";
const ICON_BUTTON_CLASS_NAME = "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[4px] border border-slate-300 bg-white text-slate-600 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-35";
const WEEKDAY_BUTTON_BASE_CLASS_NAME = "inline-flex min-h-8 min-w-11 items-center justify-center rounded-[3px] border px-2.5 py-1.5 text-xs font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30";
const NOTIFICATION_ACTION_TYPE = "notification.in_app";
const OPTIMIZATION_ACTION_TYPE = "dispatch.optimize";
const WEEKDAYS = [
  [1, "Lun"],
  [2, "Mar"],
  [3, "Mer"],
  [4, "Jeu"],
  [5, "Ven"],
  [6, "Sam"],
  [7, "Dim"],
];
const BILLING_CATEGORY_KEYS = ["missing_loaded", "missing_delivered", "ready_to_approve", "blocked"];
const BILLING_CATEGORY_CONTROLS = [
  [MISSING_ACTUAL_TONNAGE_CATEGORIES, "Tonnage réel manquant"],
  [["ready_to_approve"], "Prêt à approuver"],
  [["blocked"], "Bloqué autrement"],
];
const RUN_STATUS_LABELS = {
  failed: "Échec",
  missed: "Manquée",
  queued: "En attente",
  retrying: "Nouvel essai",
  running: "En cours",
  skipped: "Ignorée",
  succeeded: "Réussie",
};
const TASK_STATUS_LABELS = {
  active: "Active",
  completed: "Terminée",
  paused: "En pause",
};
const STATUS_TONE_CLASS_NAMES = {
  active: "border-emerald-200 bg-emerald-50 text-emerald-800",
  completed: "border-blue-200 bg-blue-50 text-blue-800",
  failed: "border-red-200 bg-red-50 text-red-800",
  missed: "border-amber-200 bg-amber-50 text-amber-800",
  paused: "border-amber-200 bg-amber-50 text-amber-800",
  queued: "border-slate-200 bg-slate-50 text-slate-700",
  retrying: "border-amber-200 bg-amber-50 text-amber-800",
  running: "border-blue-200 bg-blue-50 text-blue-800",
  skipped: "border-slate-200 bg-slate-50 text-slate-700",
  succeeded: "border-emerald-200 bg-emerald-50 text-emerald-800",
};
const TASK_FILTERS = [
  { key: "active", label: "Actives" },
  { key: "paused", label: "En pause" },
];

function clientKey() {
  return globalThis.crypto?.randomUUID?.() || `key-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function todayDateInput() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: COMPANY_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function nextHourDateTimeInput() {
  const now = Date.now();
  let candidate = new Date((Math.floor(now / 3_600_000) + 1) * 3_600_000);
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: COMPANY_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const parts = Object.fromEntries(formatter.formatToParts(candidate).map((part) => [part.type, part.value]));
    const localInput = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:00`;
    if (new Date(zonedLocalInputToIso(localInput)).getTime() > now) {
      return localInput;
    }
    candidate = new Date(candidate.getTime() + 3_600_000);
  }
  throw new Error("Impossible de proposer une prochaine heure valide.");
}

function calendarDateLabel(dateKey) {
  return new Intl.DateTimeFormat("fr-CA", {
    dateStyle: "long",
    timeZone: "UTC",
  }).format(new Date(`${dateKey}T12:00:00Z`));
}

function calendarOnceAt(dateKey) {
  return dateKey === todayDateInput() ? nextHourDateTimeInput() : `${dateKey}T08:00`;
}

function isoToLocalInput(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return nextHourDateTimeInput();
  }
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
    timeZone: COMPANY_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function zonedLocalInputToIso(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value || "");
  if (!match) {
    throw new Error("Choisissez une date et une heure valides.");
  }
  const [, year, month, day, hour, minute] = match;
  const requestedWallTime = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: COMPANY_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  for (let minutesAdvanced = 0; minutesAdvanced <= 180; minutesAdvanced += 1) {
    const targetWallTime = requestedWallTime + minutesAdvanced * 60_000;
    let guess = targetWallTime;
    for (let index = 0; index < 5; index += 1) {
      const parts = Object.fromEntries(formatter.formatToParts(new Date(guess)).map((part) => [part.type, part.value]));
      const representedWallTime = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
      const delta = targetWallTime - representedWallTime;
      guess += delta;
      if (delta === 0) {
        return new Date(guess).toISOString();
      }
    }
  }
  throw new Error("Cette heure locale ne peut pas être planifiée.");
}

function defaultNotificationAction() {
  return {
    _key: clientKey(),
    type: "notification.in_app",
    title_template: "Rappel planifié",
    body_template: "Une action requiert votre attention dans le SaaS.",
    action_url_template: "/dashboard",
    severity: "warning",
    recipients: { user_ids: [], role_keys: [] },
  };
}

function actionTypeLabel(type) {
  if (type === REPORT_EMAIL_TYPE) return "Courriel";
  if (type === OPTIMIZATION_ACTION_TYPE) return "Optimisation";
  if (type === NOTIFICATION_ACTION_TYPE) return "Notification";
  return "Action";
}

function actionSummary(action) {
  if (action.type === REPORT_EMAIL_TYPE) return `${action.subject || "Courriel"} · ${action.items?.length || 0} contenu(s)`;
  if (action.type === OPTIMIZATION_ACTION_TYPE) {
    const period = action.dispatch_period === "evening" ? "Soir" : "Jour";
    const mode = { deep: "Approfondi", fast: "Rapide", standard: "Standard" }[action.optimization_mode] || "Standard";
    return `${action.dispatch_date || "Date à choisir"} · ${period} · ${mode}`;
  }
  const recipientCount = (action.recipients?.user_ids?.length || 0) + (action.recipients?.role_keys?.length || 0);
  return `${action.title_template?.trim() || "Sans titre"} · ${recipientCount ? `${recipientCount} cible(s)` : "Aucune cible"}`;
}

function automationKindFromActions(actions = []) {
  if (actions.length !== 1) return "advanced";
  if (actions[0]?.type === OPTIMIZATION_ACTION_TYPE) return "optimization";
  if (actions[0]?.type === NOTIFICATION_ACTION_TYPE) return "notification";
  if (actions[0]?.type === REPORT_EMAIL_TYPE) return "email";
  return "advanced";
}

function automationKindFromTask(task) {
  return automationKindFromActions(task?.definition?.actions || []);
}

function defaultOptimizationAction(prefill = {}) {
  return {
    _key: clientKey(),
    type: "dispatch.optimize",
    dispatch_date: prefill.dispatchDate || todayDateInput(),
    dispatch_period: prefill.dispatchPeriod === "evening" ? "evening" : "day",
    optimization_mode: ["fast", "standard", "deep"].includes(prefill.optimizationMode) ? prefill.optimizationMode : "standard",
    include_suspended_deliveries: false,
    enforce_p2_p3_time_windows_as_hard: false,
  };
}

function createInitialForm(prefill = {}) {
  const isOptimization = prefill.actionType === "dispatch.optimize";
  const isEmail = prefill.actionType === REPORT_EMAIL_TYPE;
  const isCalendarCreation = Boolean(prefill.calendarDate);
  const billingCategories = [...BILLING_CATEGORY_KEYS];
  const billingLookbackDays = 1;
  return {
    id: null,
    status: null,
    isAdvancedConfiguration: false,
    version: null,
    name: isEmail ? prefill.reportEmailAction?.subject || "Envoi de rapports et d’indicateurs" : isOptimization ? `Optimiser le Dispatch du ${prefill.dispatchDate || todayDateInput()}` : isCalendarCreation ? "Rappel des tonnages réels" : "Rappel quotidien des tonnages réels",
    description: "",
    triggerKind: isCalendarCreation || isOptimization || isEmail ? "once" : "weekly",
    onceAt: prefill.onceAt || nextHourDateTimeInput(),
    weeklyTime: "08:00",
    weekdays: [1, 2, 3, 4, 5],
    startDate: "",
    endDate: "",
    conditionType: isOptimization || isEmail ? "always" : BILLING_PENDING_CONDITION_TYPE,
    billingCategories,
    billingLookbackDays,
    actions: isEmail ? [{ ...(prefill.reportEmailAction || defaultReportEmailAction()), _key: clientKey() }] : isOptimization
      ? [defaultOptimizationAction(prefill)]
      : [initializeNotificationMessageState({
          ...defaultNotificationAction(),
          title_template: "Tonnages réels à confirmer",
          body_template: buildBillingNotificationMessage(billingCategories, billingLookbackDays),
          action_url_template: "{{ billing.link }}",
        }, { categories: billingCategories, lookbackDays: billingLookbackDays, automatic: true })],
  };
}

function formFromTask(task, targets = null) {
  const trigger = task.trigger || {};
  const condition = task.definition?.conditions?.[0] || { type: "always" };
  const actions = task.definition?.actions || [];
  const taskKind = automationKindFromActions(actions);
  const hasConditionalOptimization = ["optimization", "email"].includes(taskKind) && condition.type !== "always";
  const billingCategories = normalizeBillingCategories(
    Array.isArray(condition.categories) ? condition.categories : BILLING_CATEGORY_KEYS,
  );
  const parsedLookbackDays = Number(condition.lookback_days);
  const billingLookbackDays = condition.through === "yesterday" && Number.isInteger(parsedLookbackDays) && parsedLookbackDays >= 1 && parsedLookbackDays <= 4
    ? parsedLookbackDays
    : "";
  const nextForm = {
    id: task.id,
    status: task.status,
    isAdvancedConfiguration: actions.length > 1 || hasConditionalOptimization,
    version: task.version,
    name: task.name,
    description: task.description || "",
    triggerKind: trigger.kind || "manual",
    onceAt: trigger.run_at ? isoToLocalInput(trigger.run_at) : nextHourDateTimeInput(),
    weeklyTime: String(trigger.local_time || "08:00").slice(0, 5),
    weekdays: Array.isArray(trigger.weekdays) ? trigger.weekdays : [1, 2, 3, 4, 5],
    startDate: trigger.start_date || "",
    endDate: trigger.end_date || "",
    conditionType: condition.type || "always",
    billingCategories,
    billingLookbackDays,
    actions: actions.map((action) => initializeNotificationMessageState(
      { ...action, _key: clientKey() },
      { categories: billingCategories, lookbackDays: billingLookbackDays || 1 },
    )),
  };
  return targets ? pruneIneligibleNotificationRecipients(nextForm, targets).form : nextForm;
}

function duplicateFormFromTask(task, targets = null) {
  const sourceForm = formFromTask(task, targets);
  const sourceRunAt = new Date(task?.trigger?.run_at || "").getTime();
  const onceAt = task?.trigger?.kind === "once" && Number.isFinite(sourceRunAt) && sourceRunAt <= Date.now()
    ? nextHourDateTimeInput()
    : sourceForm.onceAt;
  return {
    ...sourceForm,
    id: null,
    status: null,
    version: null,
    name: `Copie de ${String(task?.name || "Tâche").trim() || "Tâche"}`.slice(0, 255),
    onceAt,
  };
}

function buildPayload(form, access) {
  let trigger;
  if (form.triggerKind === "manual") {
    trigger = { kind: "manual", timezone: COMPANY_TIMEZONE };
  } else if (form.triggerKind === "once") {
    trigger = { kind: "once", timezone: COMPANY_TIMEZONE, run_at: zonedLocalInputToIso(form.onceAt) };
  } else {
    if (!form.weekdays.length) {
      throw new Error("Sélectionnez au moins un jour de la semaine.");
    }
    trigger = {
      kind: "weekly",
      timezone: COMPANY_TIMEZONE,
      weekdays: form.weekdays,
      local_time: form.weeklyTime,
      start_date: form.startDate || null,
      end_date: form.endDate || null,
    };
  }
  const conditions = form.conditionType === "always"
    ? [{ type: "always" }]
    : [buildBillingPendingCondition(form.billingCategories, form.billingLookbackDays)];
  const actions = form.actions.map((action) => {
    if (action.type !== REPORT_EMAIL_TYPE) return stripAutomationActionClientState(action);
    const error = reportEmailValidation(action);
    if (error) throw new Error(error);
    return serializeReportEmailAction(applyReportEmailCompartmentAccess(action, access));
  });
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    trigger,
    definition: { condition_mode: "all", conditions, actions },
  };
}

function formatDateTime(value) {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: COMPANY_TIMEZONE,
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function calendarDateKey(value) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
    timeZone: COMPANY_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value)).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function monthKeyFromTorontoToday() {
  return `${todayDateInput().slice(0, 7)}-01`;
}

function shiftMonthKey(monthKey, offset) {
  const [year, month] = monthKey.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + offset, 1, 12));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

function monthGrid(currentMonthKey) {
  const [year, month] = currentMonthKey.split("-").map(Number);
  const first = new Date(Date.UTC(year, month - 1, 1, 12));
  const mondayOffset = (first.getUTCDay() + 6) % 7;
  const start = new Date(first);
  start.setUTCDate(first.getUTCDate() - mondayOffset);
  const cells = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setUTCDate(start.getUTCDate() + index);
    return {
      day: day.getUTCDate(),
      key: `${day.getUTCFullYear()}-${String(day.getUTCMonth() + 1).padStart(2, "0")}-${String(day.getUTCDate()).padStart(2, "0")}`,
      month: day.getUTCMonth() + 1,
    };
  });
  return {
    cells,
    endIso: zonedLocalInputToIso(`${cells[cells.length - 1].key}T23:59`),
    month,
    startIso: zonedLocalInputToIso(`${cells[0].key}T00:00`),
  };
}

function StatusPill({ status, task = false }) {
  return (
    <span className={joinClasses([
      "inline-flex items-center rounded-[3px] border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em]",
      STATUS_TONE_CLASS_NAMES[status] || STATUS_TONE_CLASS_NAMES.queued,
    ])}>
      {(task ? TASK_STATUS_LABELS : RUN_STATUS_LABELS)[status] || status}
    </span>
  );
}

function AutomationTypeBadge({ kind, compact = false }) {
  const presentation = kind === "email"
    ? { Icon: Mail, label: "Courriel", tone: "border-[var(--color-border-accent-muted)] bg-accent-soft text-accent-strong" }
    : kind === "optimization"
    ? { Icon: Sparkles, label: "Optimisation", tone: "border-[var(--color-border-accent-muted)] bg-accent-soft text-accent-strong" }
    : kind === "notification"
      ? { Icon: Bell, label: "Notification", tone: "border-blue-200 bg-blue-50 text-blue-800" }
      : { Icon: List, label: "Configuration avancée", tone: "border-amber-200 bg-amber-50 text-amber-800" };
  const TypeIcon = presentation.Icon;
  return (
    <span
      className={joinClasses([
        "inline-flex shrink-0 items-center rounded-[4px] border font-black",
        compact ? "h-5 w-5 justify-center" : "gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em]",
        presentation.tone,
      ])}
      title={presentation.label}
    >
      <TypeIcon className="h-3 w-3" aria-hidden="true" />
      {compact ? <span className="sr-only">{presentation.label}</span> : presentation.label}
    </span>
  );
}

function WeekdayButton({ isSelected, label, onClick }) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      className={joinClasses([
        WEEKDAY_BUTTON_BASE_CLASS_NAME,
        isSelected
          ? "border-accent bg-accent text-white shadow-sm hover:border-accent-strong hover:bg-accent-strong"
          : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50",
      ])}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function ToggleCheckbox({ checked, label, onChange }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-[4px] px-1.5 py-1 text-[11px] text-slate-700 hover:bg-slate-50">
      <input type="checkbox" checked={checked} onChange={onChange} className="h-3.5 w-3.5 rounded border-slate-300 accent-accent text-accent focus:ring-accent/30" />
      <span>{label}</span>
    </label>
  );
}

function MessageTemplateField({ action, onChange }) {
  const anchorRef = React.useRef(null);
  const popoverRef = React.useRef(null);
  const popoverId = React.useId();
  const isOpen = Boolean(action._pendingGeneratedBodyTemplate);
  const keepMessage = React.useCallback(() => onChange(keepCustomNotificationBodyPatch()), [onChange]);

  usePopoverDismiss({
    anchorElement: anchorRef.current,
    floatingRef: popoverRef,
    isOpen,
    onClose: keepMessage,
  });

  return (
    <div ref={anchorRef} className="grid gap-1">
      <label className={LABEL_CLASS_NAME} htmlFor={`automation-message-${action._key}`}>Message</label>
      <textarea
        id={`automation-message-${action._key}`}
        className={`${FIELD_CLASS_NAME} min-h-16 resize-y`}
        value={action.body_template}
        onChange={(event) => onChange(notificationBodyEditPatch(action, event.target.value))}
      />
      {isOpen ? (
        <div
          id={popoverId}
          ref={popoverRef}
          role="dialog"
          aria-labelledby={`${popoverId}-title`}
          aria-describedby={`${popoverId}-description`}
          className="rounded-[4px] border border-blue-200 bg-white p-2.5 shadow-[0_14px_30px_rgba(15,23,42,0.12)]"
        >
          <p id={`${popoverId}-title`} className="m-0 text-xs font-black text-slate-900">Les critères ont changé</p>
          <p id={`${popoverId}-description`} className="m-0 mt-1 text-[11px] leading-4 text-slate-600">Une nouvelle proposition est prête. Votre message personnalisé reste intact tant que vous ne l’adaptez pas.</p>
          <div className="mt-2 flex flex-wrap justify-end gap-1.5">
            <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} onClick={keepMessage}>Conserver mon message</button>
            <button type="button" className={NOTIFICATION_BUTTON_CLASS_NAME} onClick={() => onChange(acceptGeneratedNotificationBodyPatch(action))}>Adapter le message</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function IneligibleTargetsPopover({ users, roles }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const popoverRef = React.useRef(null);
  const popoverId = React.useId();
  const count = users.length + roles.length;

  usePopoverDismiss({
    anchorElement: anchorRef.current,
    floatingRef: popoverRef,
    isOpen,
    onClose: () => setIsOpen(false),
  });

  if (count === 0) return null;
  return (
    <div ref={anchorRef} className="rounded-[4px] border border-amber-200 bg-amber-50/70 p-2">
      <button
        type="button"
        className="text-left text-[11px] font-black text-amber-900 underline decoration-amber-400 underline-offset-2"
        aria-controls={isOpen ? popoverId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => setIsOpen((current) => !current)}
      >
        Voir les non-admissibles ({count})
      </button>
      {isOpen ? (
        <div id={popoverId} ref={popoverRef} role="dialog" aria-label="Destinataires non admissibles" className="mt-2 grid max-h-48 gap-1.5 overflow-y-auto rounded-[4px] border border-amber-200 bg-white p-2 shadow-sm">
          {users.map((user) => (
            <div key={`user-${user.user_id}`} className="rounded-[4px] bg-slate-50 px-2 py-1.5">
              <p className="m-0 text-[11px] font-bold text-slate-900">{user.full_name || user.email || user.user_id}</p>
              {user.full_name && user.email ? <p className="m-0 mt-0.5 text-[10px] text-slate-500">{user.email}</p> : null}
              <p className="m-0 mt-0.5 text-[10px] leading-4 text-slate-600">{user.billing_pending_ineligibility_reason || "Droits requis manquants."}</p>
            </div>
          ))}
          {roles.map((role) => (
            <div key={`role-${role.role_key}`} className="rounded-[4px] bg-slate-50 px-2 py-1.5">
              <p className="m-0 text-[11px] font-bold text-slate-900">Rôle · {role.label}</p>
              <p className="m-0 mt-0.5 text-[10px] leading-4 text-slate-600">{role.billing_pending_ineligibility_reason || "Aucun membre admissible."}</p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function BillingConditionControls({ form, onCategoriesChange, onLookbackChange }) {
  return (
    <>
      <label className={LABEL_CLASS_NAME}>Depuis quand
        <select className={FIELD_CLASS_NAME} value={form.billingLookbackDays} onChange={(event) => onLookbackChange(event.target.value ? Number(event.target.value) : "")}>
          <option value="">Choisir une période…</option>
          <option value="1">Hier seulement</option>
          <option value="2">Les 2 dernières journées terminées</option>
          <option value="3">Les 3 dernières journées terminées</option>
          <option value="4">Les 4 dernières journées terminées</option>
        </select>
        <span className="font-normal text-slate-500">Aujourd’hui est toujours exclu.</span>
      </label>
      <div className="grid gap-1 sm:col-span-2 sm:grid-cols-3">
        {BILLING_CATEGORY_CONTROLS.map(([categories, label]) => (
          <ToggleCheckbox key={categories.join("-")} checked={categories.every((category) => form.billingCategories.includes(category))} label={label} onChange={() => onCategoriesChange(categories)} />
        ))}
      </div>
    </>
  );
}

function ActionEditor({ action, index, targets, conditionType, isExpanded, onChange, onToggle }) {
  if (action.type === REPORT_EMAIL_TYPE) return <ReportEmailComposer action={action} onChange={onChange} />;
  const updateRecipient = (group, value) => {
    const current = action.recipients?.[group] || [];
    onChange({
      recipients: {
        ...(action.recipients || { user_ids: [], role_keys: [] }),
        [group]: current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
      },
    });
  };
  const isOptimization = action.type === OPTIMIZATION_ACTION_TYPE;
  const { eligibleUsers, eligibleRoles, ineligibleUsers, ineligibleRoles } = splitAutomationTargets(targets, conditionType);
  const panelId = `automation-action-${action._key}`;
  return (
    <article className={joinClasses([
      "overflow-hidden rounded-[5px] border",
      isOptimization ? "border-[var(--color-border-accent-muted)] bg-accent-soft/40" : "border-blue-200 bg-blue-50/45",
    ])}>
      <button type="button" className="flex w-full items-center gap-2 px-2.5 py-2 text-left" aria-expanded={isExpanded} aria-controls={panelId} onClick={onToggle}>
        <span className={joinClasses(["grid h-7 w-7 shrink-0 place-items-center rounded-[4px] bg-white shadow-sm", isOptimization ? "text-accent" : "text-blue-700"])}>{isOptimization ? <Sparkles className="h-4 w-4" /> : <Bell className="h-4 w-4" />}</span>
        <span className="min-w-0 flex-1">
          <span className={joinClasses(["block text-[10px] font-black uppercase tracking-[0.12em]", isOptimization ? "text-accent-strong" : "text-blue-800"])}>Action {index + 1} · {actionTypeLabel(action.type)}</span>
          <span className="block truncate text-xs font-semibold text-slate-700">{actionSummary(action)}</span>
        </span>
        <ChevronDown className={joinClasses(["h-4 w-4 shrink-0 text-slate-500 transition-transform", isExpanded ? "rotate-180" : ""])} />
      </button>

      {isExpanded ? <div id={panelId} className="border-t border-inherit bg-white/80 p-2.5">
        {isOptimization ? (
        <div className="grid gap-2 sm:grid-cols-3">
          <label className={LABEL_CLASS_NAME}>Date du Dispatch<input className={FIELD_CLASS_NAME} type="date" value={action.dispatch_date} onChange={(event) => onChange({ dispatch_date: event.target.value })} /></label>
          <label className={LABEL_CLASS_NAME}>Période<select className={FIELD_CLASS_NAME} value={action.dispatch_period} onChange={(event) => onChange({ dispatch_period: event.target.value })}><option value="day">Jour</option><option value="evening">Soir</option></select></label>
          <label className={LABEL_CLASS_NAME}>Mode<select className={FIELD_CLASS_NAME} value={action.optimization_mode} onChange={(event) => onChange({ optimization_mode: event.target.value })}><option value="fast">Rapide</option><option value="standard">Standard</option><option value="deep">Approfondi</option></select></label>
          <div className="sm:col-span-3 grid gap-1 sm:grid-cols-2">
            <ToggleCheckbox checked={action.include_suspended_deliveries === true} label="Inclure les livraisons suspendues" onChange={(event) => onChange({ include_suspended_deliveries: event.target.checked })} />
            <ToggleCheckbox checked={action.enforce_p2_p3_time_windows_as_hard === true} label="Forcer les fenêtres P2/P3" onChange={(event) => onChange({ enforce_p2_p3_time_windows_as_hard: event.target.checked })} />
          </div>
        </div>
      ) : (
        <div className="grid gap-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className={LABEL_CLASS_NAME}>Titre<input className={FIELD_CLASS_NAME} value={action.title_template} onChange={(event) => onChange({ title_template: event.target.value })} /></label>
            <label className={LABEL_CLASS_NAME}>Importance<select className={FIELD_CLASS_NAME} value={action.severity} onChange={(event) => onChange({ severity: event.target.value })}><option value="info">Information</option><option value="success">Succès</option><option value="warning">Avertissement</option><option value="error">Erreur</option></select></label>
          </div>
          <MessageTemplateField action={action} onChange={onChange} />
          <label className={LABEL_CLASS_NAME}>Lien interne<input className={FIELD_CLASS_NAME} placeholder="/data-manager ou {{ billing.link }}" value={action.action_url_template || ""} onChange={(event) => onChange({ action_url_template: event.target.value })} /></label>
          <div className="grid gap-2 lg:grid-cols-2">
            <fieldset className="min-w-0 rounded-[4px] border border-slate-200 bg-white p-1.5">
              <legend className="px-1 text-[11px] font-black uppercase tracking-[0.1em] text-slate-500">Utilisateurs</legend>
              <div className="max-h-28 overflow-y-auto">
                {eligibleUsers.map((user) => {
                  const label = user.full_name || user.email || user.user_id;
                  return <ToggleCheckbox key={user.user_id} checked={(action.recipients?.user_ids || []).includes(user.user_id)} label={`${label}${user.active_role_label ? ` · ${user.active_role_label}` : ""}`} onChange={() => updateRecipient("user_ids", user.user_id)} />;
                })}
                {eligibleUsers.length === 0 ? <p className="m-0 px-2 py-3 text-xs text-slate-500">Aucun utilisateur admissible.</p> : null}
              </div>
            </fieldset>
            <fieldset className="min-w-0 rounded-[4px] border border-slate-200 bg-white p-1.5">
              <legend className="px-1 text-[11px] font-black uppercase tracking-[0.1em] text-slate-500">Rôles dynamiques</legend>
              <div className="max-h-28 overflow-y-auto">
                {eligibleRoles.map((role) => {
                  const eligibleMemberCount = Number(role.billing_pending_eligible_member_count) || 0;
                  const label = conditionType === BILLING_PENDING_CONDITION_TYPE
                    ? `${role.label} · ${eligibleMemberCount} membre${eligibleMemberCount > 1 ? "s" : ""}`
                    : role.label;
                  return <ToggleCheckbox key={role.role_key} checked={(action.recipients?.role_keys || []).includes(role.role_key)} label={label} onChange={() => updateRecipient("role_keys", role.role_key)} />;
                })}
                {eligibleRoles.length === 0 ? <p className="m-0 px-2 py-3 text-xs text-slate-500">Aucun rôle admissible.</p> : null}
              </div>
            </fieldset>
          </div>
          {conditionType === BILLING_PENDING_CONDITION_TYPE ? <IneligibleTargetsPopover users={ineligibleUsers} roles={ineligibleRoles} /> : null}
        </div>
        )}
      </div> : null}
    </article>
  );
}

function AutomationForm({ form, targets, preview, isBusy, emailConfigured, errorMessage, onCancel, onChange, onPreview, onSave }) {
  const inferredKind = automationKindFromActions(form.actions);
  const isAdvanced = form.isAdvancedConfiguration === true || inferredKind === "advanced";
  const automationKind = isAdvanced ? "advanced" : inferredKind;
  const isOptimization = automationKind === "optimization";
  const isEmail = automationKind === "email";
  const initialActionType = form.actions[0]?.type || NOTIFICATION_ACTION_TYPE;
  const [activeActionType, setActiveActionType] = React.useState(initialActionType);
  const [expandedActionKey, setExpandedActionKey] = React.useState(null);

  React.useEffect(() => {
    const nextActionType = form.actions[0]?.type || NOTIFICATION_ACTION_TYPE;
    setActiveActionType(nextActionType);
    setExpandedActionKey(null);
  }, [form.id]);

  React.useEffect(() => {
    if (expandedActionKey && !form.actions.some((action) => action._key === expandedActionKey)) {
      setExpandedActionKey(null);
    }
    if (isAdvanced && !form.actions.some((action) => action.type === activeActionType) && form.actions[0]) {
      setActiveActionType(form.actions[0].type);
    }
  }, [activeActionType, expandedActionKey, form.actions, isAdvanced]);

  const changeConditionType = (conditionType) => onChange((current) => pruneIneligibleNotificationRecipients(
    { ...current, conditionType },
    targets,
  ).form);
  const toggleBillingCategory = (categoryGroup) => onChange((current) => {
    const categories = toggleBillingCategoryGroup(current.billingCategories, categoryGroup);
    return applyBillingMessageCriteria(current, { categories });
  });
  const changeBillingLookback = (lookbackDays) => onChange((current) => applyBillingMessageCriteria(current, { lookbackDays }));

  const updateAction = (index, patch) => onChange((current) => ({
    ...current,
    actions: current.actions.map((action, actionIndex) => actionIndex === index ? { ...action, ...patch } : action),
  }));
  const moveAction = (index, delta) => onChange((current) => {
    const next = [...current.actions];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    return { ...current, actions: next };
  });
  const removeAction = (index, key) => {
    if (expandedActionKey === key) {
      setExpandedActionKey(null);
    }
    onChange((current) => ({ ...current, actions: current.actions.filter((_, actionIndex) => actionIndex !== index) }));
  };
  const indexedActions = form.actions.map((action, index) => ({ action, index }));
  const visibleActions = indexedActions.filter(({ action }) => action.type === activeActionType);
  const notificationCount = indexedActions.filter(({ action }) => action.type === NOTIFICATION_ACTION_TYPE).length;
  const optimizationCount = indexedActions.filter(({ action }) => action.type === OPTIMIZATION_ACTION_TYPE).length;
  const selectAction = (action) => {
    setActiveActionType(action.type);
    setExpandedActionKey(action._key);
  };
  const selectActionType = (type) => {
    setActiveActionType(type);
    setExpandedActionKey(null);
  };
  const title = isEmail ? (form.id ? "Modifier le courriel planifié" : "Courriel planifié") : isAdvanced
    ? "Configuration avancée existante"
    : isOptimization
      ? (form.id ? "Modifier l’optimisation planifiée" : "Optimisation planifiée")
      : (form.id ? "Modifier la notification planifiée" : "Notification planifiée");
  const description = isEmail ? "Choisissez les rapports, les indicateurs, les destinataires et l’heure d’envoi." : isAdvanced
    ? "Cette ancienne tâche conserve sa séquence et sa condition globale sans ajouter de nouvelle action."
    : isOptimization
      ? "Planifiez une optimisation du Dispatch selon son propre calendrier."
      : "Planifiez l’envoi d’une notification persistante selon la condition choisie.";
  const saveLabel = isEmail ? (form.id ? "Enregistrer le courriel" : "Planifier le courriel") : isAdvanced
    ? "Enregistrer la configuration"
    : isOptimization
      ? (form.id ? "Enregistrer l’optimisation" : "Planifier l’optimisation")
      : (form.id ? "Enregistrer la notification" : "Planifier la notification");
  const hasValidBillingWindow = form.conditionType !== BILLING_PENDING_CONDITION_TYPE
    || (Number.isInteger(Number(form.billingLookbackDays)) && Number(form.billingLookbackDays) >= 1 && Number(form.billingLookbackDays) <= 4);
  const hasBillingCategory = form.conditionType !== BILLING_PENDING_CONDITION_TYPE || form.billingCategories.length > 0;
  const hasEligibleNotificationRecipients = notificationActionsHaveEligibleRecipients(form, targets);
  const hasValidEmails = form.actions.every((action) => action.type !== REPORT_EMAIL_TYPE || !reportEmailValidation(action));
  const emailActivationBlocked = isEmail && emailConfigured === false && form.status !== "paused";

  return (
    <section className="rounded-[4px] border border-[var(--color-border-accent-subtle)] bg-white p-3 shadow-[0_18px_44px_rgba(15,23,42,0.08)] sm:p-3.5">
      <header className={`${isEmail ? "mb-2" : "mb-3"} flex flex-wrap items-start justify-between gap-2`}>
        <div>
          {isEmail ? <div className="flex flex-wrap items-center gap-2"><h2 className="m-0 text-base font-bold text-slate-950">{title}</h2><AutomationTypeBadge kind={automationKind} /></div> : <>
          <div className="flex flex-wrap items-center gap-2">
            <p className="m-0 text-[10px] font-black uppercase tracking-[0.17em] text-accent">Constructeur sécurisé</p>
            <AutomationTypeBadge kind={automationKind} />
          </div>
          <h2 className="m-0 mt-1 text-lg font-bold text-slate-950">{title}</h2></>}
          <p className={`m-0 mt-1 max-w-3xl text-xs ${isEmail ? "leading-4" : "leading-5"} text-slate-500`}>{description}</p>
        </div>
        <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} onClick={onCancel}><X className="h-3.5 w-3.5" /> Fermer</button>
      </header>

      <div className={`grid ${isEmail ? "gap-2" : "gap-3"}`}>
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
          <label className={LABEL_CLASS_NAME}>Nom de la tâche<input className={FIELD_CLASS_NAME} value={form.name} maxLength={255} onChange={(event) => onChange((current) => ({ ...current, name: event.target.value }))} /></label>
          <label className={LABEL_CLASS_NAME}>Description facultative<input className={FIELD_CLASS_NAME} value={form.description} onChange={(event) => onChange((current) => ({ ...current, description: event.target.value }))} /></label>
        </div>

        {isAdvanced ? <div className="rounded-[4px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900"><strong>Configuration avancée existante.</strong> La condition ci-dessous s’applique à toutes les actions, dans leur ordre d’exécution. Vous pouvez les modifier, les déplacer ou les retirer, mais pas en ajouter.</div> : null}

        <div className="grid gap-3 xl:grid-cols-[minmax(0,1.15fr)_minmax(22rem,0.85fr)] xl:items-start">
          <fieldset className="min-w-0 rounded-[4px] border border-slate-200 p-2.5">
            <legend className="px-1 text-xs font-black uppercase tracking-[0.12em] text-slate-600">1 · Calendrier</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className={LABEL_CLASS_NAME}>Déclenchement<select className={FIELD_CLASS_NAME} value={form.triggerKind} onChange={(event) => onChange((current) => ({ ...current, triggerKind: event.target.value }))}><option value="manual">Manuel seulement</option><option value="once">Une seule fois</option><option value="weekly">Jours de la semaine</option></select></label>
              {form.triggerKind === "once" ? <label className={LABEL_CLASS_NAME}>Date et heure<input className={FIELD_CLASS_NAME} type="datetime-local" value={form.onceAt} onChange={(event) => onChange((current) => ({ ...current, onceAt: event.target.value }))} /></label> : null}
              {form.triggerKind === "weekly" ? (
                <>
                  <label className={LABEL_CLASS_NAME}>Heure<input className={FIELD_CLASS_NAME} type="time" value={form.weeklyTime} onChange={(event) => onChange((current) => ({ ...current, weeklyTime: event.target.value }))} /></label>
                  <div className="sm:col-span-2 flex flex-wrap gap-1">
                    {WEEKDAYS.map(([value, label]) => {
                      const isSelected = form.weekdays.includes(value);
                      return <WeekdayButton key={value} isSelected={isSelected} label={label} onClick={() => onChange((current) => ({ ...current, weekdays: current.weekdays.includes(value) ? current.weekdays.filter((day) => day !== value) : [...current.weekdays, value].sort() }))} />;
                    })}
                  </div>
                  <label className={LABEL_CLASS_NAME}>Début facultatif<input className={FIELD_CLASS_NAME} type="date" value={form.startDate} onChange={(event) => onChange((current) => ({ ...current, startDate: event.target.value }))} /></label>
                  <label className={LABEL_CLASS_NAME}>Fin facultative<input className={FIELD_CLASS_NAME} type="date" value={form.endDate} onChange={(event) => onChange((current) => ({ ...current, endDate: event.target.value }))} /></label>
                </>
              ) : null}
            </div>
          </fieldset>

          {isAdvanced ? (
            <fieldset className="min-w-0 rounded-[4px] border border-slate-200 p-2.5">
              <legend className="px-1 text-xs font-black uppercase tracking-[0.12em] text-slate-600">2 · Condition globale</legend>
              <div className="grid gap-2 sm:grid-cols-2 sm:items-start">
                <label className={LABEL_CLASS_NAME}>Exécuter toutes les actions lorsque<select className={FIELD_CLASS_NAME} value={form.conditionType} onChange={(event) => changeConditionType(event.target.value)}><option value="always">Toujours</option><option value={BILLING_PENDING_CONDITION_TYPE}>Des tonnages réels ou approbations sont en attente</option></select></label>
                {form.conditionType === BILLING_PENDING_CONDITION_TYPE ? <BillingConditionControls form={form} onCategoriesChange={toggleBillingCategory} onLookbackChange={changeBillingLookback} /> : null}
              </div>
            </fieldset>
          ) : isEmail ? (
            <fieldset className="min-w-0 rounded-[4px] border border-[var(--color-border-accent-muted)] bg-accent-soft/35 p-2.5">
              <legend className="px-1 text-xs font-black uppercase tracking-[0.12em] text-accent-strong">2 · Envoi</legend>
              <p className="m-0 text-xs leading-4 text-slate-600">À chaque occurrence · Les contenus absents sont signalés. Aucun envoi si tous sont indisponibles.</p>
            </fieldset>
          ) : isOptimization ? (
            <fieldset className="min-w-0 rounded-[4px] border border-[var(--color-border-accent-muted)] bg-accent-soft/35 p-2.5">
              <legend className="px-1 text-xs font-black uppercase tracking-[0.12em] text-accent-strong">2 · Exécution</legend>
              <div className="flex items-start gap-2 rounded-[4px] border border-[var(--color-border-accent-muted)] bg-white px-3 py-2.5">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <div><p className="m-0 text-xs font-black text-slate-900">Lancer à chaque occurrence planifiée</p><p className="m-0 mt-0.5 text-[11px] leading-4 text-slate-500">L’optimisation démarre selon le calendrier, sans condition métier supplémentaire.</p></div>
              </div>
            </fieldset>
          ) : (
            <fieldset className="min-w-0 rounded-[4px] border border-blue-200 bg-blue-50/30 p-2.5">
              <legend className="px-1 text-xs font-black uppercase tracking-[0.12em] text-blue-800">2 · Quand envoyer la notification</legend>
              <div className="grid gap-2 sm:grid-cols-2 sm:items-start">
                <label className={LABEL_CLASS_NAME}>Envoyer lorsque<select className={FIELD_CLASS_NAME} value={form.conditionType} onChange={(event) => changeConditionType(event.target.value)}><option value="always">À chaque occurrence planifiée</option><option value={BILLING_PENDING_CONDITION_TYPE}>Des tonnages réels ou approbations sont en attente</option></select></label>
                {form.conditionType === BILLING_PENDING_CONDITION_TYPE ? <BillingConditionControls form={form} onCategoriesChange={toggleBillingCategory} onLookbackChange={changeBillingLookback} /> : null}
              </div>
            </fieldset>
          )}
        </div>

        {isAdvanced ? (
          <fieldset className="min-w-0 rounded-[4px] border border-slate-200 p-2.5">
            <legend className="px-1 text-xs font-black uppercase tracking-[0.12em] text-slate-600">3 · Actions avancées</legend>
            <div className="grid gap-2.5 lg:grid-cols-[minmax(18rem,0.7fr)_minmax(0,1.3fr)] lg:items-start">
              <section aria-labelledby="execution-sequence-title" className="rounded-[4px] border border-slate-200 bg-slate-50/70 p-2">
                <div className="mb-1.5 flex items-center justify-between gap-2"><h3 id="execution-sequence-title" className="m-0 text-[11px] font-black uppercase tracking-[0.1em] text-slate-600">Séquence d’exécution existante</h3><span className="text-[10px] font-semibold text-slate-500">{form.actions.length} action(s)</span></div>
                <ol aria-label="Séquence d’exécution existante" className="m-0 grid list-none gap-1.5 p-0">
                  {indexedActions.map(({ action, index }) => {
                    const actionIsOptimization = action.type === OPTIMIZATION_ACTION_TYPE;
                    return <li key={action._key} className={joinClasses(["flex items-center gap-1 rounded-[5px] border p-1", actionIsOptimization ? "border-[var(--color-border-accent-muted)] bg-accent-soft/55" : "border-blue-200 bg-blue-50/70"])}>
                      <button type="button" className="flex min-w-0 flex-1 items-center gap-2 rounded-[4px] px-1.5 py-1 text-left hover:bg-white/70" onClick={() => selectAction(action)}><span className={joinClasses(["grid h-5 w-5 shrink-0 place-items-center rounded-[3px] text-[10px] font-black text-white", actionIsOptimization ? "bg-accent" : "bg-blue-700"])}>{index + 1}</span><span className="min-w-0 flex-1"><span className={joinClasses(["block text-[10px] font-black uppercase tracking-[0.08em]", actionIsOptimization ? "text-accent-strong" : "text-blue-800"])}>{actionTypeLabel(action.type)}</span><span className="block truncate text-[11px] text-slate-600">{actionSummary(action)}</span></span></button>
                      <button type="button" className={ICON_BUTTON_CLASS_NAME} disabled={index === 0} aria-label={`Monter l’action ${index + 1}`} onClick={() => moveAction(index, -1)}><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button type="button" className={ICON_BUTTON_CLASS_NAME} disabled={index === form.actions.length - 1} aria-label={`Descendre l’action ${index + 1}`} onClick={() => moveAction(index, 1)}><ArrowDown className="h-3.5 w-3.5" /></button>
                      <button type="button" className={ICON_BUTTON_CLASS_NAME} disabled={form.actions.length === 1} aria-label={`Retirer l’action ${index + 1}`} onClick={() => removeAction(index, action._key)}><X className="h-3.5 w-3.5" /></button>
                    </li>;
                  })}
                </ol>
              </section>

              <section className="min-w-0">
                <div role="tablist" aria-label="Types d’actions de la configuration existante" className="mb-2 flex gap-1 rounded-[5px] bg-slate-100 p-1">
                  {indexedActions.some(({ action }) => action.type === REPORT_EMAIL_TYPE) ? <button type="button" role="tab" aria-selected={activeActionType === REPORT_EMAIL_TYPE} className={joinClasses(["min-h-8 flex-1 rounded-[4px] border px-2.5 py-1.5 text-xs font-black transition", activeActionType === REPORT_EMAIL_TYPE ? "border-accent bg-accent text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"])} onClick={() => selectActionType(REPORT_EMAIL_TYPE)}><Mail className="h-3.5 w-3.5" /> Courriels</button> : null}
                  <button type="button" role="tab" aria-selected={activeActionType === NOTIFICATION_ACTION_TYPE} aria-controls="notification-actions-panel" className={joinClasses(["min-h-8 flex-1 rounded-[4px] border px-2.5 py-1.5 text-xs font-black transition", activeActionType === NOTIFICATION_ACTION_TYPE ? "border-accent bg-accent text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"])} onClick={() => selectActionType(NOTIFICATION_ACTION_TYPE)}><Bell className="mr-1 inline h-3.5 w-3.5" /> Notifications ({notificationCount})</button>
                  <button type="button" role="tab" aria-selected={activeActionType === OPTIMIZATION_ACTION_TYPE} aria-controls="optimization-actions-panel" className={joinClasses(["min-h-8 flex-1 rounded-[4px] border px-2.5 py-1.5 text-xs font-black transition", activeActionType === OPTIMIZATION_ACTION_TYPE ? "border-accent bg-accent text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"])} onClick={() => selectActionType(OPTIMIZATION_ACTION_TYPE)}><Sparkles className="mr-1 inline h-3.5 w-3.5" /> Optimisations ({optimizationCount})</button>
                </div>
                <div id={activeActionType === NOTIFICATION_ACTION_TYPE ? "notification-actions-panel" : "optimization-actions-panel"} role="tabpanel" className="grid gap-1.5">
                  {visibleActions.map(({ action, index }) => <ActionEditor key={action._key} action={action} index={index} targets={targets} conditionType={form.conditionType} isExpanded={expandedActionKey === action._key} onChange={(patch) => updateAction(index, patch)} onToggle={() => setExpandedActionKey((current) => current === action._key ? null : action._key)} />)}
                  {visibleActions.length === 0 ? <p className="m-0 rounded-[4px] border border-dashed border-slate-300 px-3 py-3 text-center text-xs text-slate-500">Aucune {activeActionType === OPTIMIZATION_ACTION_TYPE ? "optimisation" : "notification"} dans cette configuration.</p> : null}
                </div>
              </section>
            </div>
          </fieldset>
        ) : (
          <fieldset className={joinClasses(["rounded-[4px] border p-2.5", isOptimization ? "border-[var(--color-border-accent-muted)]" : "border-blue-200"])}>
            <legend className={joinClasses(["px-1 text-xs font-black uppercase tracking-[0.12em]", isOptimization ? "text-accent-strong" : "text-blue-800"])}>3 · {isEmail ? "Courriel et contenus" : isOptimization ? "Optimisation" : "Notification"}</legend>
            {form.actions[0] ? <ActionEditor action={form.actions[0]} index={0} targets={targets} conditionType={form.conditionType} isExpanded={expandedActionKey === form.actions[0]._key} onChange={(patch) => updateAction(0, patch)} onToggle={() => setExpandedActionKey((current) => current === form.actions[0]._key ? null : form.actions[0]._key)} /> : null}
          </fieldset>
        )}

        {form.recipientEligibilityNotice ? <div className="rounded-[4px] border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">{form.recipientEligibilityNotice}</div> : null}
        {emailActivationBlocked ? <div className="rounded-[4px] border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">L’envoi par courriel est indisponible : la configuration Brevo du backend est incomplète. Vous pouvez prévisualiser le contenu, mais pas activer cette tâche.</div> : null}
        {errorMessage ? <div className="rounded-[4px] border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">{errorMessage}</div> : null}
        {preview ? (
          <div className="rounded-[4px] border border-blue-200 bg-blue-50/60 p-3.5">
            <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-700" /><h3 className="m-0 text-sm font-bold text-blue-950">Aperçu</h3></div>
            <p className="m-0 mt-2 text-xs leading-5 text-blue-900">Condition maintenant : <strong>{preview.matched_now ? "correspondante" : "aucun élément"}</strong> · Prochaine occurrence : <strong>{formatDateTime(preview.next_run_at)}</strong></p>
            {(preview.actions || []).map((action) => <p key={`${action.index}-${action.type}`} className="m-0 mt-1 text-xs leading-5 text-blue-900">Action {action.index + 1} : {action.type === REPORT_EMAIL_TYPE ? `${action.subject} · ${action.content_count} contenu(s)` : action.type === "notification.in_app" ? `${action.eligible_recipient_count} destinataire(s) · ${action.title}` : `optimisation ${action.dispatch_date} (${action.dispatch_period})`}</p>)}
            {(preview.warnings || []).map((warning) => <p key={warning} className="m-0 mt-1 text-xs font-semibold text-amber-800">{warning}</p>)}
          </div>
        ) : null}
        <footer className="flex flex-wrap justify-end gap-2">
          <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} disabled={isBusy} onClick={onPreview}>{isBusy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Prévisualiser</button>
          <button type="button" className={PRIMARY_BUTTON_CLASS_NAME} disabled={isBusy || emailActivationBlocked || !form.name.trim() || form.actions.length === 0 || !hasValidBillingWindow || !hasBillingCategory || !hasEligibleNotificationRecipients || !hasValidEmails} onClick={onSave}>{isBusy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {saveLabel}</button>
        </footer>
      </div>
    </section>
  );
}

export default function AutomationsPage() {
  const { access } = useAccess();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [tasks, setTasks] = React.useState([]);
  const [targets, setTargets] = React.useState({ users: [], roles: [] });
  const [catalog, setCatalog] = React.useState(null);
  const [health, setHealth] = React.useState(null);
  const [events, setEvents] = React.useState([]);
  const [currentMonth, setCurrentMonth] = React.useState(monthKeyFromTorontoToday);
  const [viewMode, setViewMode] = React.useState("calendar");
  const [taskStatusFilter, setTaskStatusFilter] = React.useState("active");
  const [isCompletedHistoryCollapsed, setIsCompletedHistoryCollapsed] = React.useState(true);
  const [form, setForm] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [historyTask, setHistoryTask] = React.useState(null);
  const [runs, setRuns] = React.useState([]);
  const [dateCreationMenu, setDateCreationMenu] = React.useState(null);
  const [taskContextMenu, setTaskContextMenu] = React.useState(null);
  const queryPrefillHandledRef = React.useRef("");
  const pendingCalendarClickRef = React.useRef(null);

  const grid = React.useMemo(() => monthGrid(currentMonth), [currentMonth]);
  const loadTasks = React.useCallback(async () => {
    const payload = await fetchAutomations();
    setTasks(Array.isArray(payload?.tasks) ? payload.tasks : []);
  }, []);
  const loadCalendar = React.useCallback(async () => {
    const payload = await fetchAutomationCalendar(grid.startIso, grid.endIso);
    setEvents(Array.isArray(payload?.events) ? payload.events : []);
  }, [grid.endIso, grid.startIso]);

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    Promise.all([
      fetchAutomationCatalog(),
      fetchAutomationTargets(),
      fetchAutomationHealth(),
      fetchAutomations(),
    ]).then(([catalogPayload, targetPayload, healthPayload, taskPayload]) => {
      if (cancelled) return;
      setCatalog(catalogPayload);
      setTargets(targetPayload || { users: [], roles: [] });
      setHealth(healthPayload);
      setTasks(Array.isArray(taskPayload?.tasks) ? taskPayload.tasks : []);
      setErrorMessage("");
    }).catch((error) => {
      if (!cancelled) setErrorMessage(error instanceof Error ? error.message : "Impossible de charger les tâches planifiées.");
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    void loadCalendar().catch((error) => setErrorMessage(error instanceof Error ? error.message : "Impossible de charger le calendrier."));
  }, [loadCalendar]);

  React.useEffect(() => {
    setDateCreationMenu(null);
  }, [currentMonth, viewMode]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") === REPORT_EMAIL_TYPE) {
      const key = `${location.key}:${location.search}`;
      if (queryPrefillHandledRef.current === key) return;
      queryPrefillHandledRef.current = key;
      setForm(createInitialForm({ actionType: REPORT_EMAIL_TYPE, reportEmailAction: location.state?.reportEmailAction }));
      setPreview(null);
      return;
    }
    if (params.get("new") !== "dispatch.optimize") return;
    if (queryPrefillHandledRef.current === location.search) return;
    queryPrefillHandledRef.current = location.search;
    setForm(createInitialForm({
      actionType: "dispatch.optimize",
      dispatchDate: params.get("dispatch_date") || todayDateInput(),
      dispatchPeriod: params.get("dispatch_period") || "day",
      optimizationMode: params.get("optimization_mode") || "standard",
    }));
  }, [location.key, location.search, location.state]);

  const refreshAll = React.useCallback(async () => {
    await Promise.all([loadTasks(), loadCalendar(), fetchAutomationHealth().then(setHealth)]);
  }, [loadCalendar, loadTasks]);

  React.useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void refreshAll().catch(() => {});
      }
    }, 15_000);
    return () => window.clearInterval(intervalId);
  }, [refreshAll]);
  React.useEffect(() => {
    if (!historyTask?.id) return undefined;
    let stopped = false;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      fetchAutomationRuns(historyTask.id).then((payload) => {
        if (!stopped) setRuns(Array.isArray(payload?.runs) ? payload.runs : []);
      }).catch(() => {});
    }, 5000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [historyTask?.id]);
  React.useEffect(() => () => {
    if (pendingCalendarClickRef.current !== null) window.clearTimeout(pendingCalendarClickRef.current);
  }, []);
  const submitPreview = async () => {
    setIsBusy(true);
    setErrorMessage("");
    try {
      const payload = buildPayload(form, access);
      setPreview(await previewAutomation({ trigger: payload.trigger, definition: payload.definition }));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Aperçu impossible.");
    } finally {
      setIsBusy(false);
    }
  };
  const saveForm = async () => {
    setIsBusy(true);
    setErrorMessage("");
    try {
      const payload = buildPayload(form, access);
      if (form.id) {
        await updateAutomation(form.id, { ...payload, expected_version: form.version });
        showToast({ type: "success", title: "Tâche mise à jour", message: "La prochaine occurrence utilise maintenant la nouvelle configuration." });
      } else {
        await createAutomation(payload);
        showToast({ type: "success", title: "Tâche créée", message: "La planification est active et persistante." });
      }
      setForm(null);
      setPreview(null);
      navigate("/admin/automations", { replace: true });
      await refreshAll();
    } catch (error) {
      if (error?.code === "automation_no_eligible_recipient") {
        try {
          const refreshedTargets = await fetchAutomationTargets();
          setTargets(refreshedTargets || { users: [], roles: [] });
          setForm((current) => current ? pruneIneligibleNotificationRecipients(current, refreshedTargets).form : current);
        } catch {
          // La validation serveur reste l’autorité si le rafraîchissement de l’annuaire échoue.
        }
      }
      setErrorMessage(error instanceof Error ? error.message : "Enregistrement impossible.");
    } finally {
      setIsBusy(false);
    }
  };
  const performOperation = async (task, operation) => {
    setIsBusy(true);
    try {
      await applyAutomationOperation(task.id, operation, task.version);
      await refreshAll();
      showToast({ type: "success", title: "Tâche mise à jour", message: operation === "pause" ? "La tâche est en pause." : "La planification a repris." });
    } catch (error) {
      showToast({ type: "error", title: "Action impossible", message: error instanceof Error ? error.message : "Réessayez." });
    } finally {
      setIsBusy(false);
    }
  };
  const executeNow = async (task) => {
    setIsBusy(true);
    try {
      await runAutomationNow(task.id);
      await refreshAll();
      showToast({ type: "success", title: "Exécution demandée", message: "Le worker prendra la tâche en charge dans quelques secondes." });
    } catch (error) {
      showToast({ type: "error", title: "Démarrage impossible", message: error instanceof Error ? error.message : "Réessayez." });
    } finally {
      setIsBusy(false);
    }
  };
  const openHistory = async (task) => {
    setHistoryTask(task);
    setRuns([]);
    try {
      const payload = await fetchAutomationRuns(task.id);
      setRuns(Array.isArray(payload?.runs) ? payload.runs : []);
    } catch (error) {
      showToast({ type: "error", title: "Historique indisponible", message: error instanceof Error ? error.message : "Réessayez." });
    }
  };
  const clearPendingCalendarClick = () => {
    if (pendingCalendarClickRef.current === null) return;
    window.clearTimeout(pendingCalendarClickRef.current);
    pendingCalendarClickRef.current = null;
  };
  const openEditor = (task) => {
    if (!task) return;
    clearPendingCalendarClick();
    setDateCreationMenu(null);
    setTaskContextMenu(null);
    setHistoryTask(null);
    setForm(formFromTask(task, targets));
    setPreview(null);
    setErrorMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const duplicateTask = (task) => {
    if (!task) return;
    clearPendingCalendarClick();
    setDateCreationMenu(null);
    setTaskContextMenu(null);
    setHistoryTask(null);
    setForm(duplicateFormFromTask(task, targets));
    setPreview(null);
    setErrorMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const deleteTask = async (task) => {
    if (!task || !await confirmInGrain(`La tâche « ${task.name} » et son historique seront supprimés définitivement.`, {
      title: "Supprimer cette tâche ?", confirmLabel: "Supprimer", tone: "danger",
    })) return;
    setIsBusy(true);
    try {
      await deleteAutomation(task.id, task.version);
      if (historyTask?.id === task.id) {
        setHistoryTask(null);
        setRuns([]);
      }
      if (form?.id === task.id) {
        setForm(null);
        setPreview(null);
      }
      await refreshAll();
      showToast({ type: "success", title: "Tâche supprimée", message: "La tâche et son historique ont été supprimés définitivement." });
    } catch (error) {
      showToast({ type: "error", title: "Suppression impossible", message: error instanceof Error ? error.message : "Réessayez." });
    } finally {
      setIsBusy(false);
    }
  };
  const openHistoryAfterSingleClick = (task) => {
    clearPendingCalendarClick();
    pendingCalendarClickRef.current = window.setTimeout(() => {
      pendingCalendarClickRef.current = null;
      void openHistory(task);
    }, 220);
  };
  const openTaskContextMenu = (event, task) => {
    if (!task) return;
    event.preventDefault();
    event.stopPropagation();
    clearPendingCalendarClick();
    setDateCreationMenu(null);
    setTaskContextMenu({ task, position: { x: event.clientX, y: event.clientY } });
  };
  const openDateCreationMenu = (dateKey, position) => {
    clearPendingCalendarClick();
    setTaskContextMenu(null);
    if (dateKey < todayDateInput()) {
      setDateCreationMenu(null);
      showToast({
        type: "info",
        title: "Date passée",
        message: "Cette date est passée. Choisissez aujourd’hui ou une date future pour planifier une tâche.",
      });
      return;
    }
    setDateCreationMenu({
      dateKey,
      dateLabel: calendarDateLabel(dateKey),
      position,
    });
  };
  const openDateCreationMenuFromPointer = (event, dateKey) => {
    if (event.target !== event.currentTarget && event.target.closest("[data-automation-calendar-event]")) return;
    event.preventDefault();
    openDateCreationMenu(dateKey, { x: event.clientX, y: event.clientY });
  };
  const openDateCreationMenuFromKeyboard = (event, dateKey) => {
    if (event.target !== event.currentTarget || !["Enter", " "].includes(event.key)) return;
    event.preventDefault();
    const bounds = event.currentTarget.getBoundingClientRect();
    openDateCreationMenu(dateKey, {
      x: bounds.left + Math.min(bounds.width / 2, 48),
      y: bounds.top + Math.min(bounds.height / 2, 36),
    });
  };
  const createFromCalendarDate = (actionType) => {
    const dateKey = dateCreationMenu?.dateKey;
    if (!dateKey) return;
    setDateCreationMenu(null);
    setTaskContextMenu(null);
    setHistoryTask(null);
    setRuns([]);
    setForm(createInitialForm({
      actionType,
      calendarDate: dateKey,
      onceAt: calendarOnceAt(dateKey),
      dispatchDate: actionType === OPTIMIZATION_ACTION_TYPE ? dateKey : undefined,
    }));
    setPreview(null);
    setErrorMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const eventsByDay = React.useMemo(() => events.reduce((result, event) => {
    const key = calendarDateKey(event.starts_at);
    result[key] = [...(result[key] || []), event];
    return result;
  }, {}), [events]);
  const tasksById = React.useMemo(() => new Map(tasks.map((task) => [task.id, task])), [tasks]);
  const filteredTasks = React.useMemo(
    () => tasks.filter((task) => task.status === taskStatusFilter),
    [taskStatusFilter, tasks],
  );
  const completedTasks = React.useMemo(
    () => tasks.filter((task) => task.status === "completed").sort((left, right) =>
      (Date.parse(right.completed_at || right.last_run_at) || 0) - (Date.parse(left.completed_at || left.last_run_at) || 0)),
    [tasks],
  );
  const renderTaskCard = (task) => {
    const emailItems = (task.definition?.actions || []).filter((action) => action.type === REPORT_EMAIL_TYPE).flatMap((action) => action.items || []);
    return (
      <article key={task.id} data-automation-task-status={task.status} className="rounded-[4px] border border-slate-200 p-3.5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1 basis-72 break-words">
            <div className="flex flex-wrap items-center gap-2"><h3 className="m-0 text-sm font-bold text-slate-950">{task.name}</h3><AutomationTypeBadge kind={automationKindFromTask(task)} /><StatusPill status={task.status} task /></div>
            {emailItems.length ? <div className="mt-1 grid gap-0.5 text-xs leading-5 text-slate-700">
              <span className="font-semibold">Contenu du courriel :</span>
              {emailItems.map((item, index) => <p key={index} className="m-0">{REPORT_EMAIL_LABELS[item.key] || item.key} · {item.date_mode === "fixed" ? item.fixed_date : ({ today: "Aujourd’hui", yesterday: "Hier", tomorrow: "Demain" })[item.date_mode]} · Dispatch {item.dispatch_period === "evening" ? "Soir" : "Jour"}</p>)}
            </div> : null}
            <p className="m-0 mt-1 text-xs leading-5 text-slate-500">{task.status === "completed" ? "Terminée : " : "Prochaine : "}{formatDateTime(task.status === "completed" ? task.completed_at : task.next_run_at)} · Dernière : {task.last_run_status ? RUN_STATUS_LABELS[task.last_run_status] || task.last_run_status : "—"}{task.last_run_status ? " (" + formatDateTime(task.last_run_at) + ")" : ""}</p>
            {task.description ? <p className="m-0 mt-1 text-xs text-slate-600">{task.description}</p> : null}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} disabled={isBusy} onClick={() => void executeNow(task)}><Play className="h-3.5 w-3.5" /> Exécuter</button>
            {task.status === "active" ? <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} disabled={isBusy} onClick={() => void performOperation(task, "pause")}><Pause className="h-3.5 w-3.5" /> Pause</button> : task.status === "paused" ? <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} disabled={isBusy} onClick={() => void performOperation(task, "resume")}><Play className="h-3.5 w-3.5" /> Reprendre</button> : null}
            <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} disabled={isBusy} onClick={() => openEditor(task)}><Pencil className="h-3.5 w-3.5" /> Modifier</button>
            <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} disabled={isBusy} onClick={() => duplicateTask(task)}><Copy className="h-3.5 w-3.5" /> Dupliquer</button>
            <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} disabled={isBusy} onClick={() => void openHistory(task)}><History className="h-3.5 w-3.5" /> Historique</button>
            <button type="button" className={DANGER_BUTTON_CLASS_NAME} disabled={isBusy} onClick={() => void deleteTask(task)}><Trash2 className="h-3.5 w-3.5" /> Supprimer</button>
          </div>
        </div>
      </article>
    );
  };
  const todayKey = todayDateInput();
  const activeCreationKind = form?.id === null ? automationKindFromActions(form.actions) : null;
  const monthLabel = new Intl.DateTimeFormat("fr-CA", { month: "long", year: "numeric", timeZone: COMPANY_TIMEZONE }).format(new Date(`${currentMonth}T12:00:00Z`));

  if (!canManageAutomations(access)) {
    return null;
  }

  return (
    <div className={joinClasses(["mx-auto grid w-full min-w-0 grid-cols-1 gap-3 px-4 pb-6 pt-0", PAGE_WIDTH_CLASS_NAME])}>
      <WorkspacePageHeader truckBanner icon={Clock3} title="Tâches planifiées" description="Planifier des courriels de rapports, des notifications et des optimisations, avec historique d’exécution." actions={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canSendReportsEmail(access) ? <button type="button" aria-pressed={activeCreationKind === "email"} className={joinClasses([NEW_AUTOMATION_BUTTON_CLASS_NAME, activeCreationKind === "email" ? NEW_AUTOMATION_BUTTON_SELECTED_CLASS_NAME : NEW_AUTOMATION_BUTTON_IDLE_CLASS_NAME])} onClick={() => { setForm(createInitialForm({ actionType: REPORT_EMAIL_TYPE })); setPreview(null); setErrorMessage(""); }}><Mail className="h-4 w-4" /> Nouveau courriel</button> : null}
            <button type="button" aria-pressed={activeCreationKind === "notification"} className={joinClasses([NEW_AUTOMATION_BUTTON_CLASS_NAME, activeCreationKind === "notification" ? NEW_AUTOMATION_BUTTON_SELECTED_CLASS_NAME : NEW_AUTOMATION_BUTTON_IDLE_CLASS_NAME])} onClick={() => { setForm(createInitialForm({ actionType: NOTIFICATION_ACTION_TYPE })); setPreview(null); setErrorMessage(""); }}><Bell className="h-4 w-4" /> Nouvelle notification</button>
            <button type="button" aria-pressed={activeCreationKind === "optimization"} className={joinClasses([NEW_AUTOMATION_BUTTON_CLASS_NAME, activeCreationKind === "optimization" ? NEW_AUTOMATION_BUTTON_SELECTED_CLASS_NAME : NEW_AUTOMATION_BUTTON_IDLE_CLASS_NAME])} onClick={() => { setForm(createInitialForm({ actionType: OPTIMIZATION_ACTION_TYPE })); setPreview(null); setErrorMessage(""); }}><Plus className="h-4 w-4" /> Nouvelle optimisation</button>
          </div>
      )} />

      {health && (!health.enabled || !health.healthy) ? <div className="rounded-[4px] border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs font-semibold text-amber-900">Le moteur de planification est dégradé{health.error ? ` : ${health.error}` : "."} Les tâches restent enregistrées, mais leur exécution peut être retardée.</div> : null}
      {health?.email?.configured === false ? <div role="alert" className="rounded-[4px] border border-red-200 bg-red-50 px-3 py-2.5 text-xs font-semibold text-red-900">L’envoi par courriel est indisponible. La configuration Brevo du backend est incomplète{health.email.missing?.length ? ` (${health.email.missing.join(", ")})` : ""}.</div> : null}
      {targets.directory?.warning ? <div className="rounded-[4px] border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">Annuaire : {targets.directory.warning}</div> : null}
      {form ? <AutomationForm form={form} targets={targets} preview={preview} isBusy={isBusy} emailConfigured={health?.email?.configured} errorMessage={errorMessage} onCancel={() => { setForm(null); setPreview(null); navigate("/admin/automations", { replace: true }); }} onChange={setForm} onPreview={() => void submitPreview()} onSave={() => void saveForm()} /> : null}

      <section className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-[0_12px_32px_rgba(15,23,42,0.05)]">
        <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-accent" /><div><h2 className="m-0 text-base font-bold capitalize text-slate-950">{monthLabel}</h2><p className="m-0 text-[11px] text-slate-500">Double-cliquez sur une date pour créer une tâche · Fuseau {COMPANY_TIMEZONE} · catalogue v{catalog?.version || 1}</p></div></div>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} onClick={() => setCurrentMonth((current) => shiftMonthKey(current, -1))}><ChevronLeft className="h-3.5 w-3.5" /></button>
            <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} onClick={() => setCurrentMonth(monthKeyFromTorontoToday())}>Aujourd’hui</button>
            <button type="button" className={SECONDARY_BUTTON_CLASS_NAME} onClick={() => setCurrentMonth((current) => shiftMonthKey(current, 1))}><ChevronRight className="h-3.5 w-3.5" /></button>
            <button type="button" className={joinClasses([SECONDARY_BUTTON_CLASS_NAME, viewMode === "calendar" ? "border-accent! bg-accent! text-white! hover:border-accent-strong! hover:bg-accent-strong! hover:text-white!" : ""])} onClick={() => setViewMode("calendar")}><CalendarDays className="h-3.5 w-3.5" /> Calendrier</button>
            <button type="button" className={joinClasses([SECONDARY_BUTTON_CLASS_NAME, viewMode === "agenda" ? "border-accent! bg-accent! text-white! hover:border-accent-strong! hover:bg-accent-strong! hover:text-white!" : ""])} onClick={() => setViewMode("agenda")}><List className="h-3.5 w-3.5" /> Agenda</button>
          </div>
        </header>
        {viewMode === "calendar" ? (
          <div className="overflow-x-auto">
            <div className="min-w-190">
              <div className="grid grid-cols-7 border-b border-slate-200">{WEEKDAYS.map(([, label]) => <div key={label} className="px-2 py-1 text-center text-[10px] font-black uppercase tracking-[0.1em] text-slate-500">{label}</div>)}</div>
              <div className="grid grid-cols-7">
                {grid.cells.map((day) => {
                  const dayEvents = eventsByDay[day.key] || [];
                  const inMonth = day.month === grid.month;
                  const isPast = day.key < todayKey;
                  const dateLabel = calendarDateLabel(day.key);
                  return (
                    <div
                      key={day.key}
                      role="group"
                      tabIndex={0}
                      aria-label={`${dateLabel}. ${isPast ? "Date passée; la création est indisponible." : "Double-cliquez ou appuyez sur Entrée ou Espace pour créer une tâche."}`}
                      data-calendar-date={day.key}
                      data-calendar-date-past={isPast ? "true" : "false"}
                      title={isPast ? "Cette date est passée" : `Créer une tâche le ${dateLabel}`}
                      className={joinClasses([
                        "min-h-20 border-b border-r border-slate-100 p-1 outline-hidden transition focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40",
                        inMonth ? "bg-white" : "bg-slate-50/70",
                        isPast ? "cursor-not-allowed" : "cursor-pointer hover:bg-slate-50",
                      ])}
                      onDoubleClick={(event) => openDateCreationMenuFromPointer(event, day.key)}
                      onKeyDown={(event) => openDateCreationMenuFromKeyboard(event, day.key)}
                    >
                      <span className={`text-[11px] font-bold ${inMonth ? "text-slate-700" : "text-slate-400"}`}>{day.day}</span>
                      <div className="mt-1 grid gap-1">
                        {dayEvents.slice(0, 4).map((event) => {
                          const task = tasksById.get(event.task_id);
                          const eventKind = automationKindFromTask(task);
                          const eventTone = event.status === "failed" || event.status === "missed"
                            ? "border-red-200 bg-red-50 text-red-800"
                            : event.status === "succeeded"
                              ? "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
                              : "border-blue-200 bg-blue-50 text-blue-800";
                          const eventTime = new Intl.DateTimeFormat("fr-CA", { timeZone: COMPANY_TIMEZONE, hour: "2-digit", minute: "2-digit" }).format(new Date(event.starts_at));
                          return <button key={`${event.kind}-${event.run_id || event.task_id}-${event.starts_at}`} type="button" data-automation-calendar-event="true" className={joinClasses(["flex min-w-0 items-center gap-1 rounded-[4px] border px-1.5 py-1 text-left text-[10px] font-bold", eventTone])} title={`${event.task_name} · ${formatDateTime(event.starts_at)}`} onClick={() => { if (task) openHistoryAfterSingleClick(task); }} onDoubleClick={(doubleClickEvent) => { doubleClickEvent.stopPropagation(); openEditor(task); }} onContextMenu={(contextEvent) => openTaskContextMenu(contextEvent, task)}><AutomationTypeBadge kind={eventKind} compact /><span className="min-w-0 truncate">{eventTime} · {event.task_name}</span></button>;
                        })}
                        {dayEvents.length > 4 ? <span className="text-[10px] font-semibold text-slate-500">+{dayEvents.length - 4}</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {events.length === 0 ? <p className="m-0 py-8 text-center text-sm text-slate-500">Aucune occurrence dans cette période.</p> : events.map((event) => {
              const task = tasksById.get(event.task_id);
              return <button key={`${event.kind}-${event.run_id || event.task_id}-${event.starts_at}`} type="button" className="flex w-full items-center justify-between gap-3 px-2 py-3 text-left hover:bg-slate-50" onClick={() => { if (task) openHistoryAfterSingleClick(task); }} onDoubleClick={(doubleClickEvent) => { doubleClickEvent.stopPropagation(); openEditor(task); }} onContextMenu={(contextEvent) => openTaskContextMenu(contextEvent, task)}><span className="min-w-0"><span className="flex items-center gap-2"><AutomationTypeBadge kind={automationKindFromTask(task)} /><strong className="truncate text-sm text-slate-900">{event.task_name}</strong></span><span className="mt-1 block text-xs text-slate-500">{formatDateTime(event.starts_at)}</span></span><StatusPill status={event.status === "planned" ? "queued" : event.status} /></button>;
            })}
          </div>
        )}
      </section>

      <section className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-[0_12px_32px_rgba(15,23,42,0.05)]">
        <header className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div><h2 className="m-0 text-base font-bold text-slate-950">Tâches enregistrées</h2><p className="m-0 mt-0.5 text-xs text-slate-500">Les tâches actives sont affichées ici. Les tâches en pause restent accessibles avec le filtre.</p></div>
          <div role="tablist" aria-label="Filtrer les tâches enregistrées" className="flex flex-wrap items-center gap-1.5">
            {TASK_FILTERS.map((filter) => {
              const isSelected = taskStatusFilter === filter.key;
              return <button key={filter.key} type="button" role="tab" aria-selected={isSelected} aria-controls="saved-automation-tasks" className={joinClasses(["min-h-8 rounded-[4px] border px-2.5 py-1.5 text-xs font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30", isSelected ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50"])} onClick={() => setTaskStatusFilter(filter.key)}>{filter.label}</button>;
            })}
          </div>
        </header>
        <div id="saved-automation-tasks" role="tabpanel">
          {isLoading ? <div className="grid min-h-28 place-items-center"><LoaderCircle className="h-5 w-5 animate-spin text-slate-500" /></div> : errorMessage && !form ? <p className="m-0 rounded-[4px] border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-800">{errorMessage}</p> : tasks.length === 0 ? <p className="m-0 py-8 text-center text-sm text-slate-500">Aucune tâche enregistrée.</p> : filteredTasks.length === 0 ? <p className="m-0 py-8 text-center text-sm text-slate-500">Aucune tâche {taskStatusFilter === "paused" ? "en pause" : "active"}.</p> : <div className="grid gap-2">{filteredTasks.map(renderTaskCard)}</div>}
        </div>
      </section>

      <section aria-labelledby="completed-automation-tasks-title" className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-[0_12px_32px_rgba(15,23,42,0.05)]">
        <header className={joinClasses(["flex items-center gap-2", !isCompletedHistoryCollapsed && "mb-3"])}>
          <button type="button" onClick={() => setIsCompletedHistoryCollapsed((current) => !current)} aria-expanded={!isCompletedHistoryCollapsed} aria-controls="completed-automation-tasks-content" aria-label={isCompletedHistoryCollapsed ? "Déplier l’historique des tâches terminées" : "Replier l’historique des tâches terminées"} className={joinClasses([ICON_BUTTON_CLASS_NAME, "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"])}>
            <ChevronRight size={13} aria-hidden="true" className={isCompletedHistoryCollapsed ? "" : "rotate-90"} />
          </button>
          <div className="min-w-0"><h2 id="completed-automation-tasks-title" className="m-0 text-base font-bold text-slate-950">Historique des tâches terminées</h2><p className="m-0 mt-0.5 text-xs text-slate-500">Les planifications terminées sont conservées ici, avec leur historique d’exécution.</p></div>
        </header>
        <div id="completed-automation-tasks-content" hidden={isCompletedHistoryCollapsed}>
          {isLoading ? <div className="grid min-h-28 place-items-center"><LoaderCircle className="h-5 w-5 animate-spin text-slate-500" /></div> : errorMessage && !form ? <p className="m-0 rounded-[4px] border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-800">{errorMessage}</p> : completedTasks.length === 0 ? <p className="m-0 py-6 text-center text-sm text-slate-500">Aucune tâche terminée.</p> : <div className="grid gap-2">{completedTasks.map(renderTaskCard)}</div>}
        </div>
      </section>

      {historyTask ? <section className="rounded-[4px] border border-slate-200 bg-white p-4 shadow-[0_12px_32px_rgba(15,23,42,0.05)]"><header className="mb-3 flex items-center justify-between gap-3"><div><p className="m-0 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Historique</p><div className="mt-1 flex flex-wrap items-center gap-2"><h2 className="m-0 text-base font-bold text-slate-950">{historyTask.name}</h2><AutomationTypeBadge kind={automationKindFromTask(historyTask)} /></div></div><button type="button" className={SECONDARY_BUTTON_CLASS_NAME} onClick={() => setHistoryTask(null)}><X className="h-3.5 w-3.5" /> Fermer</button></header>{runs.length === 0 ? <p className="m-0 py-6 text-center text-sm text-slate-500">Aucune exécution enregistrée.</p> : <div className="divide-y divide-slate-100">{runs.map((run) => <div key={run.id} className="grid gap-1 py-3 sm:grid-cols-[12rem_7rem_minmax(0,1fr)] sm:items-center"><span className="text-xs text-slate-600">{formatDateTime(run.scheduled_for)}</span><StatusPill status={run.status} /><span className="text-xs text-slate-600">{run.error_message || `${run.action_results?.length || 0} action(s) terminée(s)`}</span><div className="sm:col-span-3"><ReportEmailRunDetails run={run} /></div></div>)}</div>}</section> : null}
      <AutomationDateCreationMenu
        canCreateEmail={canSendReportsEmail(access)}
        dateLabel={dateCreationMenu?.dateLabel}
        position={dateCreationMenu?.position}
        onClose={() => setDateCreationMenu(null)}
        onSelect={createFromCalendarDate}
      />
      <AutomationTaskContextMenu
        busy={isBusy}
        task={taskContextMenu?.task}
        position={taskContextMenu?.position}
        onClose={() => setTaskContextMenu(null)}
        onDelete={(task) => void deleteTask(task)}
        onDuplicate={duplicateTask}
        onEdit={openEditor}
        onHistory={(task) => void openHistory(task)}
        onExecute={(task) => void executeNow(task)}
        onPause={(task) => void performOperation(task, "pause")}
        onResume={(task) => void performOperation(task, "resume")}
      />
    </div>
  );
}
